import React, { useEffect, useState } from 'react'
import { MDXProvider } from '@mdx-js/react'
import { evaluate } from '@mdx-js/mdx'
import * as runtime from 'react/jsx-runtime'
import * as mdxReact from '@mdx-js/react'
import remarkGfm from 'remark-gfm'
import { Heading, Link, Message, Text } from '@workwhile/ui'
import './styles.css'

interface MarkdownRendererProps {
  markdown: string
  ref?: React.RefObject<HTMLElement | null>
}

export function preprocessMarkdown(content: string): string {
  // Remove broken/incomplete JSX-style tags (e.g. "<br", "<123", "<@abc")
  const contentWithRemovedBrokenTags = content.replace(
    /<[^>\s/][^>\n\r]{0,10}(?=\s|$)/g,
    ''
  )

  // Escape lone < not part of valid tags (e.g. in "x < 5")
  const fullyEscaped = contentWithRemovedBrokenTags.replace(
    /<(?![A-Za-z/!])/g,
    '&lt;'
  )

  return fullyEscaped
}

const components: Readonly<Parameters<typeof MDXProvider>[0]['components']> = {
  h1: (props: any) => <Heading level={2} as="h1" {...props} />,
  h2: (props: any) => <Heading level={3} as="h2" {...props} />,
  h3: (props: any) => <Heading level={4} as="h3" fontWeight="500" {...props} />,
  h4: (props: any) => <Heading level={4} as="h4" fontWeight="500" {...props} />,
  h5: (props: any) => <Heading level={4} as="h5" fontWeight="500" {...props} />,
  h6: (props: any) => <Heading level={4} as="h6" fontWeight="500" {...props} />,
  p: (props: any) => <Text as="p" {...props} />,
  ul: (props: any) => (
    <ul {...props} style={{ listStyle: 'inside', ...props.style }} />
  ),
  ol: (props: any) => (
    <ol {...props} style={{ listStyle: 'inside', ...props.style }} />
  ),
  li: (props: any) => (
    <li {...props} style={{ paddingLeft: '0.5rem', ...props.style }} />
  ),
  a: ({ href, children, ...props }) => {
    const isInternal = href?.startsWith('/')
    if (isInternal) {
      return (
        <Link to={href} {...props} target="_blank">
          {children}
        </Link>
      )
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    )
  },
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  markdown,
  ref,
}) => {
  // We'll store the compiled component here
  const [MDXComponent, setMDXComponent] = useState<
    Awaited<ReturnType<typeof evaluate>>['default'] | null
  >(null)

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      try {
        const processedMarkdown = preprocessMarkdown(markdown)
        const { default: Comp } = await evaluate(processedMarkdown, {
          ...runtime,
          ...mdxReact,
          remarkPlugins: [remarkGfm],
          rehypePlugins: [],
        })

        if (!cancelled) {
          setMDXComponent(() => Comp)
        }
      } catch (err) {
        console.error('MDX evaluate error:', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [markdown])

  if (!MDXComponent) {
    return (
      <Message variant="error" size="small">
        Unable to render content. Please notify engineering.
      </Message>
    )
  }

  return (
    <MDXProvider components={components}>
      <article className="mdx-article" ref={ref}>
        <MDXComponent components={components} />
      </article>
    </MDXProvider>
  )
}
